import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { BookOpen, Pencil, Save, X, ArrowLeft, Home, Trophy, BarChart3, Shield } from 'lucide-react';

const Regolamento = () => {
  const navigate = useNavigate();
  let user = null;
  try {
    const auth = useAuth();
    user = auth?.user;
  } catch (e) { /* not authenticated */ }
  const [content, setContent] = useState('');
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    api.get('/regolamento')
      .then(res => setContent(res.data?.content || ''))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/regolamento', { content: editContent });
      setContent(editContent);
      setEditing(false);
      toast.success('Regolamento aggiornato!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen ept-bg-ambient">
      {/* Navigation bar */}
      <nav className="sticky top-0 z-40 bg-[#0a0d15]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Indietro</span>
          </button>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate('/')} className="p-2 text-slate-500 hover:text-[#00F0FF] transition-colors" title="Home">
              <Home size={18} />
            </button>
            <button onClick={() => navigate('/classifica')} className="p-2 text-slate-500 hover:text-[#FFEA00] transition-colors" title="Classifica">
              <BarChart3 size={18} />
            </button>
            <button onClick={() => navigate('/tornei')} className="p-2 text-slate-500 hover:text-[#00F0FF] transition-colors" title="Tornei">
              <Shield size={18} />
            </button>
          </div>
        </div>
      </nav>

      <div className="space-y-6 max-w-4xl mx-auto p-4 md:p-6 lg:p-8">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen size={24} className="text-[#00F0FF]" />
          <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-white">
            Regolamento
          </h1>
        </div>
        {isSuperAdmin && !editing && (
          <Button
            onClick={() => { setEditContent(content); setEditing(true); }}
            className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/20"
            data-testid="edit-regolamento-btn"
          >
            <Pencil size={16} className="mr-2" /> Modifica
          </Button>
        )}
      </motion.div>

      {editing ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full min-h-[500px] bg-[#0a0d15] border border-white/10 text-white p-4 rounded-lg font-mono text-sm leading-relaxed resize-y focus:border-[#00F0FF]/40 focus:outline-none"
            placeholder="Scrivi il regolamento qui..."
            data-testid="regolamento-editor"
          />
          <p className="text-xs text-slate-500">
            Supporta formattazione: **grassetto**, *corsivo*, # Titolo, ## Sottotitolo, - Lista puntata, 1. Lista numerata
          </p>
          <div className="flex gap-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary"
              data-testid="save-regolamento-btn"
            >
              {saving ? (
                <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Salvataggio...</span>
              ) : (
                <><Save size={16} className="mr-2" /> Salva Regolamento</>
              )}
            </Button>
            <Button
              variant="outline"
              className="border-white/10"
              onClick={() => setEditing(false)}
            >
              <X size={16} className="mr-2" /> Annulla
            </Button>
          </div>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {content ? (
            <div className="bg-[#0a0d15] border border-white/10 rounded-lg p-6" data-testid="regolamento-content">
              <div className="prose prose-invert max-w-none">
                {content.split('\n').map((line, i) => {
                  if (!line.trim()) return <br key={i} />;
                  // Headers
                  if (line.startsWith('### ')) return <h3 key={i} className="text-lg font-bold text-[#00F0FF] mt-6 mb-2 uppercase tracking-wider">{line.slice(4)}</h3>;
                  if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-bold text-[#FFEA00] mt-8 mb-3 uppercase tracking-wider">{line.slice(3)}</h2>;
                  if (line.startsWith('# ')) return <h1 key={i} className="text-2xl font-bold text-white mt-8 mb-4 uppercase tracking-wider border-b border-white/10 pb-2">{line.slice(2)}</h1>;
                  // Lists
                  if (line.match(/^\d+\.\s/)) return <p key={i} className="text-slate-300 pl-4 py-0.5 flex gap-2"><span className="text-[#00F0FF] font-bold flex-shrink-0">{line.match(/^\d+/)[0]}.</span><span>{renderInline(line.replace(/^\d+\.\s/, ''))}</span></p>;
                  if (line.startsWith('- ')) return <p key={i} className="text-slate-300 pl-4 py-0.5 flex gap-2"><span className="text-[#00F0FF]">&bull;</span><span>{renderInline(line.slice(2))}</span></p>;
                  // Normal paragraph
                  return <p key={i} className="text-slate-300 leading-relaxed py-0.5">{renderInline(line)}</p>;
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-16 bg-[#0a0d15] border border-white/10 rounded-lg">
              <BookOpen size={48} className="mx-auto text-slate-600 mb-4" />
              <p className="text-slate-500">Nessun regolamento inserito</p>
              {isSuperAdmin && (
                <Button
                  className="mt-4 bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF]"
                  onClick={() => { setEditContent(''); setEditing(true); }}
                >
                  <Pencil size={16} className="mr-2" /> Scrivi Regolamento
                </Button>
              )}
            </div>
          )}
        </motion.div>
      )}
      </div>
    </div>
  );
};

// Inline formatting: **bold**, *italic*
function renderInline(text) {
  const parts = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const italicMatch = remaining.match(/\*(.+?)\*/);
    const match = boldMatch && (!italicMatch || boldMatch.index <= italicMatch.index) ? boldMatch : italicMatch;
    if (!match) {
      parts.push(remaining);
      break;
    }
    if (match.index > 0) {
      parts.push(remaining.slice(0, match.index));
    }
    if (match[0].startsWith('**')) {
      parts.push(<strong key={key++} className="text-white font-bold">{match[1]}</strong>);
    } else {
      parts.push(<em key={key++} className="text-[#00F0FF]/80">{match[1]}</em>);
    }
    remaining = remaining.slice(match.index + match[0].length);
  }
  return parts;
}

export default Regolamento;
